import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./Hospital.css";
import HospitalCreatePopup from "./Popup/HospitalCreatePopup";
import HospitalModifyPopup from "./Popup/HospitalModifyPopup";
import HospitalSchedulePopup from "./Popup/HospitalSchedulePopup";
import { BtnCellRenderer } from "../../../components/Util/AgGridButton";
import * as hospitalFunctions from "../../../functions/Functions";
import { formatAccess } from "../../../components/Util/DateFormat";
import { getServerBaseUrl } from "../../../utils/portConfig";
import Swal from "sweetalert2";

function Hospitals() {
  // State 및 상수 정의
  const [hospitalOrders, setHospitalOrders] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSchedulePopupOpen, setIsSchedulePopupOpen] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [gridReload, setGridReload] = useState(false);

  // 테이블 높이 설정값
  const [pageSize, setPageSize] = useState(10); // 기본값 10으로 시작
  const gridContainerRef = useRef(null);

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1/hospital`;
  const auth = localStorage.getItem("auth");
  const token = localStorage.getItem("token");
  const id = localStorage.getItem("id");
  const userId = localStorage.getItem("userid");

  const hflag = "hospital";

  // 그리드 컬럼 정의
  const columns = [
    { field: "hospitalId", headerName: "병원 ID", width: 150, cellStyle: { textAlign: "left" } },
    { field: "hospitalName", headerName: "병원명", width: 150, cellStyle: { textAlign: "left" } },
    { field: "hospitalAddress", headerName: "병원 소재지", flex: 1, cellStyle: { textAlign: "left" }, tooltipValueGetter: (params) => params.data.hospitalAddress },
    { field: "hospitalSchedule", headerName: "당직/휴진 관리", width: 140, cellRenderer: BtnCellRenderer },
    { field: "hospitalTelNo", headerName: "병원 전화번호", width: 135 },
    { field: "countOfDoctor", headerName: "병원 소속 직원수", width: 155 },
    { field: "modifiedDt", headerName: "병원정보 마지막 수정일", width: 190, valueFormatter: (params) => formatAccess(params.data.modifiedDt) },
    { field: "id", hide: true },
  ];

  // 그리드 옵션 정의
  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  // 그리드 초기화 함수
  const onGridReady = () => {
    // 그리드 초기화 로직 추가
  };

  // 병원 데이터 조회 함수
  const fetchHospitalData = async () => {
    try {
      Swal.fire({ title: "데이터를 가져오는 중...", text: "잠시만 기다려 주세요.", didOpen: () => Swal.showLoading(), allowOutsideClick: false, allowEscapeKey: false, showConfirmButton: false });
      const data = await hospitalFunctions.loadData(`${API_SERVER_URL}/list/${id}`, token);
      setHospitalOrders(data);
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire("오류!", "데이터를 가져오는 중 오류가 발생했습니다.", "error");
    } finally {
      setTimeout(() => Swal.close(), 500);
    }
  };

  // useEffect를 이용한 초기 데이터 조회
  useEffect(() => {
    fetchHospitalData();
  }, []);

  // 그리드 데이터 갱신 useEffect
  useEffect(() => {
    fetchHospitalData();
  }, [API_SERVER_URL, token, gridReload]);

  // 그리드 행 선택 시 처리
  const handleRowSelected = async (event) => {
    try {
      const hospitalDetail = await hospitalFunctions.getUserDetail(event.data.links.self.href, token);
      setSelectedRow(hospitalDetail);
    } catch (error) {
      console.error("Error fetching hospital detail:", error);
    }
  };

  // 그리드 특정 셀 선택 시 처리
  const handleCellClicked = (event) => {
    const clickedColumnId = event.column.colId;
    if (clickedColumnId !== "hospitalSchedule") {
      hospitalFunctions.OpenModifyPopup(setIsPopupOpen, setPopupType);
    } else {
      setIsSchedulePopupOpen(true);
    }
  };

  // 생성 팝업 오픈 함수
  const openCreatePopup = () => {
    hospitalFunctions.OpenCreatePopup(setIsPopupOpen, setPopupType);
  };

  // 팝업 닫기 함수
  const closePopup = () => {
    hospitalFunctions.ClosePopup(setIsPopupOpen);
    setIsSchedulePopupOpen(false);
    setSelectedRow(null);
    setGridReload((prev) => !prev);
  };

  // 생성 함수
  const savePopup = async (hospitalData) => {
    hospitalFunctions.handleSaveHospitalPopup(API_SERVER_URL, token, userId, hospitalData, setIsPopupOpen, setGridReload);
  };

  // 수정 함수
  const editPopup = async (hospitalData) => {
    hospitalFunctions.handleEditHospitalPopup(API_SERVER_URL, token, userId, hospitalData, setIsPopupOpen, setGridReload, setSelectedRow);
  };

  // 삭제 함수
  const handleDelete = () => {
    hospitalFunctions.deleteInfoFunction(selectedRow, token, hflag, setGridReload, setSelectedRow, setIsPopupOpen);
  };

  // 테이블 높이 변경 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // 컴포넌트 마운트 및 창 크기 변경 시 테이블 높이 조정
  useEffect(() => {
    calculatePageSize();

    // 창 크기 변경 이벤트 리스너 추가
    window.addEventListener("resize", calculatePageSize);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("resize", calculatePageSize);
    };
  }, []);

  // 데이터 로드 후 그리드 크기에 맞게 테이블 높이 재계산
  useEffect(() => {
    if (hospitalOrders.length > 0) {
      setTimeout(calculatePageSize, 100); // 데이터 로드 후 약간의 지연을 두고 계산
    }
  }, [hospitalOrders]);

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>병원 관리</h2>
        </div>

        {auth === "Superadmin" && (
          <div className="search-container">
            <div className="search-container">
              <div className="button-group">
                <button className="dashboard-content-button blue-button" onClick={openCreatePopup}>
                  생성
                </button>
              </div>
            </div>
          </div>
        )}

        <HospitalCreatePopup isOpen={isPopupOpen && popupType === "create"} onClose={closePopup} onSave={savePopup} />
        <HospitalModifyPopup isOpen={isPopupOpen && popupType === "modify"} onClose={closePopup} onSave={editPopup} selectedRow={selectedRow} onDelete={handleDelete} />
        <HospitalSchedulePopup isOpen={isSchedulePopupOpen} onClose={closePopup} selectedRow={selectedRow} />

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          <AgGridReact
            onGridReady={onGridReady}
            columnDefs={columns}
            pagination={true}
            paginationPageSize={pageSize}
            defaultColDef={defaultColDef}
            tooltipMouseTrack={true}
            enableBrowserTooltips={true}
            rowData={hospitalOrders}
            onRowSelected={handleRowSelected}
            onCellClicked={handleCellClicked}
            rowSelection="single"
            domLayout="autoHeight"
            multiSortKey="shift"
          />
        </div>
      </div>
    </div>
  );
}

export default Hospitals;
